"use client";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import {
  ChevronRight,
  ClipboardList,
  Check,
  Loader2,
  Circle,
} from "lucide-react";
import * as React from "react";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface PlanContextValue {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  isStreaming: boolean;
}

const PlanContext = React.createContext<PlanContextValue | null>(null);

function usePlan() {
  const ctx = React.useContext(PlanContext);
  if (!ctx) throw new Error("Plan sub-components must be used within <Plan />");
  return ctx;
}

// ---------------------------------------------------------------------------
// <Plan />
// ---------------------------------------------------------------------------

interface PlanProps extends React.ComponentProps<"div"> {
  isStreaming?: boolean;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function Plan({
  children,
  className,
  isStreaming = false,
  open,
  defaultOpen = true,
  onOpenChange,
  ...props
}: PlanProps) {
  const [isOpen, setIsOpenState] = React.useState(defaultOpen);
  const actualOpen = open !== undefined ? open : isOpen;

  const setIsOpen = React.useCallback(
    (value: boolean) => {
      setIsOpenState(value);
      onOpenChange?.(value);
    },
    [onOpenChange]
  );

  return (
    <PlanContext.Provider value={{ isOpen: actualOpen, setIsOpen, isStreaming }}>
      <div
        className={cn(
          "rounded-xl border border-[color:var(--card-border)] bg-[color:var(--card-bg)] overflow-hidden",
          isStreaming && "border-[color:var(--nt-primary)]/30",
          className
        )}
        {...props}
      >
        <Collapsible open={actualOpen} onOpenChange={setIsOpen}>
          {children}
        </Collapsible>
      </div>
    </PlanContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// <PlanHeader />
// ---------------------------------------------------------------------------

export function PlanHeader({
  children,
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("px-4 pt-3 pb-2", className)}
      {...props}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// <PlanTitle />
// ---------------------------------------------------------------------------

export function PlanTitle({
  children,
  className,
  ...props
}: React.ComponentProps<"div">) {
  const { isStreaming } = usePlan();

  return (
    <div
      className={cn(
        "flex items-center gap-2 text-sm font-semibold text-[color:var(--foreground)]",
        isStreaming && "animate-pulse",
        className
      )}
      {...props}
    >
      <ClipboardList className="h-4 w-4 shrink-0 text-[color:var(--nt-primary)]" />
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// <PlanDescription />
// ---------------------------------------------------------------------------

export function PlanDescription({
  children,
  className,
  ...props
}: React.ComponentProps<"p">) {
  const { isStreaming } = usePlan();

  return (
    <p
      className={cn(
        "mt-1 text-xs text-[color:var(--foreground-muted)] leading-relaxed",
        isStreaming && "animate-pulse",
        className
      )}
      {...props}
    >
      {children}
    </p>
  );
}

// ---------------------------------------------------------------------------
// <PlanTrigger />
// ---------------------------------------------------------------------------

export function PlanTrigger({
  children,
  className,
  ...props
}: React.ComponentProps<typeof CollapsibleTrigger>) {
  return (
    <CollapsibleTrigger
      className={cn(
        "group inline-flex items-center gap-1.5 text-xs font-medium transition-colors",
        "text-[color:var(--foreground-muted)] hover:text-[color:var(--foreground)]",
        "ml-4 mb-2",
        className
      )}
      {...props}
    >
      <ChevronRight className="h-3 w-3 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-90" />
      {children || "Ver etapas"}
    </CollapsibleTrigger>
  );
}

// ---------------------------------------------------------------------------
// <PlanContent />
// ---------------------------------------------------------------------------

export function PlanContent({
  children,
  className,
  ...props
}: React.ComponentProps<typeof CollapsibleContent>) {
  return (
    <CollapsibleContent
      className={cn(
        "overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down",
        className
      )}
      {...props}
    >
      <div className="px-4 pb-3 space-y-0">{children}</div>
    </CollapsibleContent>
  );
}

// ---------------------------------------------------------------------------
// <PlanStep />
// ---------------------------------------------------------------------------

type PlanStepStatus = "complete" | "active" | "pending";

interface PlanStepProps extends React.ComponentProps<"div"> {
  label: string;
  description?: string;
  status?: PlanStepStatus;
  isLast?: boolean;
}

export function PlanStep({
  label,
  description,
  status = "pending",
  isLast = false,
  className,
  children,
  ...props
}: PlanStepProps) {
  return (
    <div
      className={cn("relative flex items-start gap-3 pb-2.5", className)}
      {...props}
    >
      {/* Vertical connector */}
      {!isLast && (
        <div
          className={cn(
            "absolute left-[11px] top-6 bottom-0 w-px",
            status === "complete"
              ? "bg-emerald-300/60 dark:bg-emerald-700/40"
              : status === "active"
              ? "bg-[color:var(--nt-primary)]/30"
              : "bg-[color:var(--card-border)]"
          )}
        />
      )}

      {/* Step icon */}
      <div
        className={cn(
          "relative z-10 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border",
          status === "complete" &&
            "border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-950/40",
          status === "active" &&
            "border-[color:var(--nt-primary)]/40 bg-[color:var(--nt-primary)]/10",
          status === "pending" &&
            "border-[color:var(--card-border)] bg-[color:var(--card-bg)]"
        )}
      >
        {status === "complete" ? (
          <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
        ) : status === "active" ? (
          <Loader2 className="h-3 w-3 animate-spin text-[color:var(--nt-primary)]" />
        ) : (
          <Circle className="h-2.5 w-2.5 text-[color:var(--foreground-muted)]" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pt-0.5">
        <div
          className={cn(
            "text-[13px] font-medium leading-tight",
            status === "complete" && "text-[color:var(--foreground)]",
            status === "active" && "text-[color:var(--nt-primary)]",
            status === "pending" && "text-[color:var(--foreground-muted)]"
          )}
        >
          {label}
        </div>
        {description && (
          <p
            className={cn(
              "mt-0.5 text-xs leading-relaxed",
              status === "pending"
                ? "text-[color:var(--foreground-muted)]/50"
                : "text-[color:var(--foreground-muted)]"
            )}
          >
            {description}
          </p>
        )}
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// <PlanFooter />
// ---------------------------------------------------------------------------

export function PlanFooter({
  children,
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 px-4 py-2.5 border-t border-[color:var(--card-border)] bg-[color:var(--card-bg)]",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// <PlanAction />
// ---------------------------------------------------------------------------

interface PlanActionProps extends React.ComponentProps<"button"> {
  variant?: "primary" | "secondary";
  loading?: boolean;
}

export function PlanAction({
  children,
  className,
  variant = "primary",
  loading = false,
  disabled,
  ...props
}: PlanActionProps) {
  return (
    <button
      className={cn(
        "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors",
        variant === "primary" &&
          "bg-[color:var(--nt-primary)] text-white hover:opacity-90 disabled:opacity-50",
        variant === "secondary" &&
          "border border-[color:var(--card-border)] text-[color:var(--foreground-muted)] hover:text-[color:var(--foreground)] hover:bg-[color:var(--card-bg)]",
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Loader2 className="h-3 w-3 animate-spin" />}
      {children}
    </button>
  );
}
